// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * What the application shows before it knows who is asking (**ADR-0075**).
 *
 * Four states, and the distinction between the last two is the point:
 *
 * - **resolving** — a bounded loading state while `GET /api/v1/session` is in
 *   flight. Chrome is not rendered yet, because rendering navigation before
 *   the session resolves would flash administration affordances at someone who
 *   may turn out to be unauthenticated;
 * - **authenticated** — the Control Center, with navigation gated by the
 *   capabilities the *server* returned;
 * - **unauthenticated** — the sign-in screen, with no navigation and no Sign
 *   Out; and
 * - **unavailable** — the session could not be read at all. Reported as a
 *   service fault rather than as "signed out": telling an operator to sign in
 *   again during an outage sends them to a login that cannot work either, and
 *   during a Redis outage it would look exactly like a revocation.
 */

import { LoadingState, useSession } from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import type { ReactNode } from 'react';

import { BootstrapLoginScreen } from './BootstrapLoginScreen';

/** Props for {@link SessionGate}. */
export interface SessionGateProps {
  /** The authenticated application. */
  readonly children: ReactNode;
}

/** Render the application, the sign-in screen, or a service state. */
export function SessionGate({ children }: SessionGateProps): ReactNode {
  const { session, isLoading, error } = useSession();

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <LoadingState label="Checking your session" />
      </Container>
    );
  }

  // An error reading the session is a service fault. `GET /session` answers 200
  // with `authenticated: false` for "nobody is signed in", so reaching here
  // means the BFF could not answer at all.
  if (error !== undefined) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }} component="main">
        <Alert severity="warning" role="alert" data-testid="session-unavailable">
          The service is unavailable and your session could not be checked. This is not a sign-out —
          try again shortly.
        </Alert>
      </Container>
    );
  }

  if (session?.authenticated !== true) {
    return <BootstrapLoginScreen />;
  }

  return <>{children}</>;
}
